/**
 * Google Analytics 4 Service
 * Centralized analytics tracking for Settler
 */

import ReactGA from 'react-ga4';
import { logger } from '../utils/logger';

class AnalyticsService {
  private initialized = false;

  /**
   * Initialize GA4 with measurement ID
   */
  initialize() {
    const measurementId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    
    if (!measurementId) {
      logger.warn('⚠️  GA4 Measurement ID not found. Analytics disabled.');
      return;
    }

    try {
      ReactGA.initialize(measurementId, {
        gaOptions: {
          anonymize_ip: true, // GDPR compliance
        },
        gtagOptions: {
          send_page_view: false, // We'll manually track page views
        },
      });
      
      this.initialized = true;
      logger.debug('✅ GA4 initialized:', measurementId);
    } catch (error) {
      logger.error('❌ Failed to initialize GA4:', error);
    }
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title?: string) {
    if (!this.initialized) return;

    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
  }

  /**
   * Track app opened event with entry source
   */
  trackAppOpened(source?: string, chatId?: string) {
    if (!this.initialized) return;

    this.trackEvent('app_opened', {
      source: source || 'direct',
      chat_id: chatId,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track user registration
   */
  trackRegistration(userId: string, username?: string) {
    if (!this.initialized) return;

    this.trackEvent('sign_up', {
      method: 'telegram',
      user_id: userId,
      username: username,
    });
  }

  /**
   * Track expense added
   */
  trackExpenseAdded(data: {
    amount: number;
    category: string;
    groupId: string;
    splitType: 'equal' | 'custom';
    inputMethod: 'smart' | 'manual';
  }) {
    if (!this.initialized) return;

    this.trackEvent('expense_added', {
      amount: data.amount,
      category: data.category,
      group_id: data.groupId,
      split_type: data.splitType,
      input_method: data.inputMethod,
    });
  }

  /**
   * Track expense deleted
   */
  trackExpenseDeleted(expenseId: string, amount: number) {
    if (!this.initialized) return;

    this.trackEvent('expense_deleted', {
      expense_id: expenseId,
      amount: amount,
    });
  }

  /**
   * Track settlement (manual or crypto)
   */
  trackSettlement(data: {
    amount: number;
    method: 'manual' | 'ton' | 'usdt';
    groupId: string;
    fromUserId: string;
    toUserId: string;
  }) {
    if (!this.initialized) return;

    this.trackEvent('settlement_completed', {
      amount: data.amount,
      payment_method: data.method,
      group_id: data.groupId,
      currency: data.method === 'manual' ? 'USD' : data.method.toUpperCase(),
    });

    // Also track as e-commerce purchase for crypto payments
    if (data.method === 'ton' || data.method === 'usdt') {
      this.trackPurchase({
        transaction_id: `${data.fromUserId}_${data.toUserId}_${Date.now()}`,
        value: data.amount,
        currency: data.method.toUpperCase(),
        items: [{
          item_id: data.groupId,
          item_name: 'Debt Settlement',
          item_category: 'Settlement',
          price: data.amount,
          quantity: 1,
        }],
      });
    }
  }

  /**
   * Track TON wallet connection
   */
  trackWalletConnected(walletAddress: string) {
    if (!this.initialized) return;

    this.trackEvent('wallet_connected', {
      wallet_type: 'ton',
      wallet_address: walletAddress.slice(0, 8) + '...', // Privacy
    });
  }

  /**
   * Track group creation
   */
  trackGroupCreated(groupId: string, memberCount: number) {
    if (!this.initialized) return;

    this.trackEvent('group_created', {
      group_id: groupId,
      member_count: memberCount,
    });
  }

  /**
   * Track group joined via invite
   */
  trackGroupJoined(groupId: string, source: 'invite_link' | 'telegram_group') {
    if (!this.initialized) return;

    this.trackEvent('group_joined', {
      group_id: groupId,
      join_source: source,
    });
  }

  /**
   * Track FAQ viewed
   */
  trackFAQViewed(question: string) {
    if (!this.initialized) return;

    this.trackEvent('faq_viewed', {
      question: question,
    });
  }

  /**
   * Track error/issue
   */
  trackError(error: string, context?: string) {
    if (!this.initialized) return;

    this.trackEvent('error_occurred', {
      error_message: error,
      error_context: context,
    });
  }

  /**
   * Set user properties (called once after login)
   */
  setUserProperties(userId: string, properties: {
    username?: string;
    firstName?: string;
    isPremium?: boolean;
    languageCode?: string;
  }) {
    if (!this.initialized) return;

    ReactGA.set({
      user_id: userId,
      user_properties: {
        telegram_username: properties.username,
        telegram_premium: properties.isPremium ? 'yes' : 'no',
        language: properties.languageCode || 'unknown',
      },
    });
  }

  /**
   * Generic event tracking
   */
  private trackEvent(eventName: string, params?: Record<string, any>) {
    if (!this.initialized) return;

    ReactGA.event(eventName, params);
  }

  /**
   * Track e-commerce purchase (for TON/USDT payments)
   */
  private trackPurchase(data: {
    transaction_id: string;
    value: number;
    currency: string;
    items: Array<{
      item_id: string;
      item_name: string;
      item_category: string;
      price: number;
      quantity: number;
    }>;
  }) {
    if (!this.initialized) return;

    ReactGA.event('purchase', {
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
      items: data.items,
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

